const PROFILE_CACHE_KEY = 'mp_profile_cache';
const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;
const PROFILE_CHANGED_EVENT = 'mp-profile-changed';

export const isBrowser = () => typeof window !== 'undefined';

const safeParseJson = (value) => {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const toStringSafe = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text || fallback;
};

const toIntSafe = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const toBooleanSafe = (value, fallback = false) => {
    if (value === true || value === false) return value;
    if (value === 1 || value === '1') return true;
    if (value === 0 || value === '0') return false;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', 'yes', 'y', 'active', 'aktif'].includes(normalized)) return true;
        if (['false', 'no', 'n', 'inactive', 'tidak aktif'].includes(normalized)) return false;
    }
    return fallback;
};

const formatNumberId = (value, fallback = '0') => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toLocaleString('id-ID') : fallback;
};

export const getProfileCacheKey = () => PROFILE_CACHE_KEY;

export const getProfileChangedEventName = () => PROFILE_CHANGED_EVENT;

export const getProfileAvatarUrl = (profile) => {
    const candidate =
        profile?.avatar_url ?? profile?.avatar ?? profile?.profile?.avatar ?? profile?.profile?.avatar_url ?? '';

    const avatar = toStringSafe(candidate, '');

    if (!avatar) return '';

    if (
        avatar.startsWith('http://') ||
        avatar.startsWith('https://') ||
        avatar.startsWith('data:') ||
        avatar.startsWith('blob:')
    ) {
        return avatar;
    }

    if (avatar.startsWith('/')) {
        return avatar;
    }

    return `/${avatar.replace(/^\/+/, '')}`;
};

export const getProfileDisplayName = (profile) => {
    return (
        toStringSafe(profile?.display_name, '') ||
        toStringSafe(profile?.name, '') ||
        toStringSafe(profile?.full_name, '') ||
        'User'
    );
};

export const getProfileInitial = (profile) => {
    const name = getProfileDisplayName(profile);
    return name.charAt(0).toUpperCase() || 'U';
};

export const getProfileSubscriptionLabel = (profile) => {
    const subscription = profile?.subscription ?? null;

    const planName =
        toStringSafe(subscription?.plan?.name, '') ||
        toStringSafe(subscription?.plan_name, '') ||
        toStringSafe(subscription?.name, '') ||
        toStringSafe(subscription?.plan?.title, '');

    const planCode =
        toStringSafe(subscription?.plan?.code, '') ||
        toStringSafe(subscription?.plan_code, '') ||
        toStringSafe(subscription?.code, '');

    if (planName) return planName;
    if (planCode) return planCode.toUpperCase();

    return 'Tanpa Paket';
};

export const isProfileSubscriptionActive = (profile) => {
    const subscription = profile?.subscription ?? null;

    const status = toStringSafe(subscription?.status, '').toLowerCase();

    if (subscription?.is_active === true) return true;
    if (toBooleanSafe(subscription?.is_active, false) === true) return true;

    return ['active', 'aktif', 'trial', 'trialing', 'grace'].includes(status);
};

export const getProfileSubscriptionStatusLabel = (profile) => {
    return isProfileSubscriptionActive(profile) ? 'Aktif' : 'Tidak aktif';
};

export const getProfileQuotaSummary = (profile) => {
    const quota = profile?.subscription?.quota?.properties ?? null;

    if (!quota) {
        return {
            limit: null,
            used: null,
            remaining: null,
            is_unlimited: false,
            label: '',
            usageText: '0/0',
            remainingText: '0 tersisa',
            usagePercent: null,
        };
    }

    const isUnlimited = Boolean(quota.is_unlimited);
    const limit = isUnlimited ? null : toIntSafe(quota.limit, 0);
    const used = toIntSafe(quota.used, 0);
    const remaining = isUnlimited ? null : toIntSafe(quota.remaining, 0);

    const usagePercent =
        !isUnlimited && Number.isFinite(limit) && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : null;

    const label = isUnlimited
        ? 'Unlimited'
        : remaining === null || remaining === undefined
          ? formatNumberId(used)
          : `${formatNumberId(remaining)} tersisa`;

    return {
        limit,
        used,
        remaining,
        is_unlimited: isUnlimited,
        label,
        usageText: isUnlimited ? 'Unlimited' : `${formatNumberId(used)}/${formatNumberId(limit)}`,
        remainingText: isUnlimited ? 'Tidak terbatas' : `${formatNumberId(remaining)} tersisa`,
        usagePercent,
    };
};

export const normalizeUserSubscription = (subscription) => {
    if (!subscription || typeof subscription !== 'object') {
        return null;
    }

    const plan = subscription.plan && typeof subscription.plan === 'object' ? subscription.plan : null;

    const price = subscription.price && typeof subscription.price === 'object' ? subscription.price : null;

    const quota = subscription.quota && typeof subscription.quota === 'object' ? subscription.quota : null;

    const propertiesQuota = quota?.properties && typeof quota.properties === 'object' ? quota.properties : null;

    const features = Array.isArray(subscription.features)
        ? subscription.features.map((feature) => ({
              key: toStringSafe(feature?.key, ''),
              label: toStringSafe(feature?.label, ''),
              value: feature?.value ?? null,
              type: toStringSafe(feature?.type, ''),
          }))
        : [];

    const normalizedPlan = plan
        ? {
              id: plan.id ?? null,
              code: toStringSafe(plan.code, ''),
              name: toStringSafe(plan.name, ''),
              slug: toStringSafe(plan.slug, ''),
              description: toStringSafe(plan.description, ''),
              is_default: Boolean(plan.is_default),
              is_featured: Boolean(plan.is_featured),
              is_unlimited: Boolean(plan.is_unlimited),
              property_limit:
                  plan.property_limit === null || plan.property_limit === undefined
                      ? null
                      : toIntSafe(plan.property_limit, null),
              featured_property_limit:
                  plan.featured_property_limit === null || plan.featured_property_limit === undefined
                      ? null
                      : toIntSafe(plan.featured_property_limit, null),
          }
        : null;

    return {
        id: subscription.id ?? null,
        status: toStringSafe(subscription.status, ''),
        starts_at: subscription.starts_at ?? null,
        ends_at: subscription.ends_at ?? null,
        next_billing_at: subscription.next_billing_at ?? null,
        auto_renew: Boolean(subscription.auto_renew),
        is_active: toBooleanSafe(subscription.is_active, false),
        plan: normalizedPlan,
        price: price
            ? {
                  billing_cycle: toStringSafe(price.billing_cycle, ''),
                  duration_months:
                      price.duration_months === null || price.duration_months === undefined
                          ? null
                          : toIntSafe(price.duration_months, null),
                  price: price.price ?? null,
                  currency: toStringSafe(price.currency, 'IDR'),
              }
            : null,
        quota: {
            properties: propertiesQuota
                ? {
                      limit:
                          propertiesQuota.limit === null || propertiesQuota.limit === undefined
                              ? null
                              : toIntSafe(propertiesQuota.limit, null),
                      used: toIntSafe(propertiesQuota.used, 0),
                      remaining: propertiesQuota.is_unlimited === true ? null : toIntSafe(propertiesQuota.remaining, 0),
                      is_unlimited: Boolean(propertiesQuota.is_unlimited),
                  }
                : null,
        },
        features,
    };
};

export const normalizeUserProfile = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    return {
        id: payload.id ?? null,
        name: toStringSafe(payload.name, ''),
        email: toStringSafe(payload.email, ''),
        phone_number: toStringSafe(payload.phone_number, ''),
        status: toStringSafe(payload.status, ''),
        avatar: payload.avatar ?? null,
        avatar_url: getProfileAvatarUrl(payload),
        bio: payload.bio ?? null,
        address: payload.address ?? null,
        city: payload.city ?? null,
        province: payload.province ?? null,
        postal_code: payload.postal_code ?? null,
        whatsapp_verified_at: payload.whatsapp_verified_at ?? null,
        last_login_at: payload.last_login_at ?? null,
        created_at: payload.created_at ?? null,
        display_name: getProfileDisplayName(payload),
        initial: getProfileInitial(payload),
        subscription: normalizeUserSubscription(payload.subscription),
    };
};

export const readProfileCache = () => {
    if (!isBrowser()) return null;

    const raw = window.localStorage.getItem(PROFILE_CACHE_KEY);
    const cached = raw ? safeParseJson(raw) : null;

    if (!cached || typeof cached !== 'object') return null;
    if (!cached.data) return null;

    return {
        data: normalizeUserProfile(cached.data),
        cachedAt: Number(cached.cachedAt) || Date.now(),
    };
};

export const writeProfileCache = (data) => {
    if (!isBrowser()) return;

    const normalized = normalizeUserProfile(data);

    window.localStorage.setItem(
        PROFILE_CACHE_KEY,
        JSON.stringify({
            data: normalized,
            cachedAt: Date.now(),
        })
    );

    window.dispatchEvent(new Event(PROFILE_CHANGED_EVENT));
};

export const clearProfileCache = () => {
    if (!isBrowser()) return;

    window.localStorage.removeItem(PROFILE_CACHE_KEY);
    window.dispatchEvent(new Event(PROFILE_CHANGED_EVENT));
};

export const isProfileCacheStale = (cachedAt) => {
    if (!cachedAt) return true;
    return Date.now() - cachedAt > PROFILE_CACHE_TTL_MS;
};

export const PROFILE_CACHE_TTL = PROFILE_CACHE_TTL_MS;

export const getSubscriptionUpgradePrompt = (profile) => {
    const planLabel = getProfileSubscriptionLabel(profile);
    const planStatus = getProfileSubscriptionStatusLabel(profile);
    const quota = getProfileQuotaSummary(profile);

    const subscription = profile?.subscription ?? null;
    const planCode = toStringSafe(subscription?.plan?.code, '').toUpperCase();
    const planName = toStringSafe(subscription?.plan?.name, '').toLowerCase();

    const isFreePlan =
        planCode === 'FREE' ||
        Boolean(subscription?.plan?.is_default) ||
        planName === 'free' ||
        planLabel.toLowerCase() === 'tanpa paket';

    const isActive = isProfileSubscriptionActive(profile);

    const isNearLimit = quota?.is_unlimited !== true && typeof quota?.remaining === 'number' && quota.remaining <= 1;

    const base = {
        show: false,
        tone: 'success',
        icon: 'success',
        title: '',
        description: '',
        ctaLabel: '',
        ctaTo: '',
        planLabel,
        planStatus,
        quotaLabel: quota?.label || '',
        quotaUsageText: quota?.usageText || '',
        planCode: planCode || (isFreePlan ? 'FREE' : ''),
        planDescription: toStringSafe(subscription?.plan?.description, ''),
    };

    if (!subscription) {
        return {
            ...base,
            show: true,
            tone: 'warning',
            icon: 'warning',
            title: 'Belum ada paket aktif',
            description:
                'Aktifkan paket subscription agar Anda bisa memaksimalkan fitur dan memperluas kapasitas listing.',
            ctaLabel: 'Pilih Paket',
            ctaTo: '/dashboard/subscription',
            planLabel: 'Tanpa Paket',
            planStatus: 'Tidak aktif',
            quotaLabel: '0 tersisa',
            quotaUsageText: '0/0',
        };
    }

    if (!isActive) {
        return {
            ...base,
            show: true,
            tone: 'danger',
            icon: 'danger',
            title: 'Subscription tidak aktif',
            description: 'Segera perpanjang atau upgrade agar layanan tetap berjalan normal.',
            ctaLabel: 'Aktifkan Sekarang',
            ctaTo: '/dashboard/subscription',
        };
    }

    if (isFreePlan) {
        return {
            ...base,
            show: true,
            tone: isNearLimit ? 'warning' : 'info',
            icon: isNearLimit ? 'warning' : 'info',
            title: isNearLimit ? 'Kuota paket FREE hampir habis' : `Anda masih menggunakan paket ${planLabel}`,
            description:
                `Paket saat ini: ${planLabel}. ` +
                `Status: ${planStatus}. ` +
                `Kuota listing: ${quota?.usageText || '0/0'}${quota?.remainingText ? ` • ${quota.remainingText}` : ''}. ` +
                'Upgrade sekarang untuk menambah kapasitas listing, meningkatkan visibilitas, dan membuka fitur premium.',
            ctaLabel: 'Upgrade Paket',
            ctaTo: '/dashboard/subscription-plans',
        };
    }

    return base;
};
